import { NavLink } from "react-router";
import { motion } from "motion/react";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Compass,
  DollarSign,
  GraduationCap,
  MapPin,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center text-center py-20 px-6 max-w-sm mx-auto"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mb-5 shadow-xl shadow-violet-500/20">
        <Compass className="w-9 h-9 text-white" />
      </div>
      <h2
        className="text-[22px] text-slate-900 mb-2 tracking-tight"
        style={{ fontWeight: 800 }}
      >
        No Course Finder Result Yet
      </h2>
      <p
        className="text-[14px] text-slate-500 leading-relaxed mb-7"
        style={{ fontWeight: 400 }}
      >
        Complete the Course Finder to save your study interests, preferred
        location, budget, and course suggestions.
      </p>
      <NavLink
        to="/course-finder"
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-md hover:-translate-y-0.5 transition-all"
        style={{ fontWeight: 700 }}
      >
        Start Course Finder <ArrowRight className="w-4 h-4" />
      </NavLink>
    </motion.div>
  );
}

export default function CourseFinderResultPage() {
  const { user } = useAuth();
  const result = user?.courseFinderResult;
  const answers = result?.answers;

  if (!answers) {
    return (
      <div className="p-4 lg:p-8 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1
            className="text-[24px] lg:text-[30px] text-slate-900 tracking-tight"
            style={{ fontWeight: 900 }}
          >
            Course Finder
          </h1>
          <p
            className="text-[13px] text-slate-500 mt-1"
            style={{ fontWeight: 400 }}
          >
            Your saved study preferences and course direction.
          </p>
        </div>
        <EmptyState />
      </div>
    );
  }

  const completedDate = result.completedAt
    ? new Date(result.completedAt).toLocaleDateString("en-MY", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const summaryItems = [
    {
      icon: <GraduationCap className="w-4 h-4" />,
      label: "Qualification",
      value: answers.qualification,
    },
    {
      icon: <BookOpen className="w-4 h-4" />,
      label: "Estimated Result",
      value: answers.estimatedResult,
    },
    {
      icon: <MapPin className="w-4 h-4" />,
      label: "Study Location",
      value: answers.preferredStudyLocations?.join(", "),
    },
    {
      icon: <Compass className="w-4 h-4" />,
      label: "Pathway",
      value: answers.pathway,
    },
    {
      icon: <DollarSign className="w-4 h-4" />,
      label: "Budget",
      value: answers.budget,
    },
  ].filter((item) => item.value);

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="mb-6"
      >
        <h1
          className="text-[24px] lg:text-[30px] text-slate-900 tracking-tight"
          style={{ fontWeight: 900 }}
        >
          Course Finder
        </h1>
        <p
          className="text-[13px] text-slate-500 mt-1"
          style={{ fontWeight: 400 }}
        >
          {completedDate
            ? `Completed on ${completedDate}`
            : "Your saved study preferences and course direction."}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.05 }}
        className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-7 text-white mb-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <p
              className="text-[12px] text-violet-200 uppercase tracking-widest mb-1"
              style={{ fontWeight: 700 }}
            >
              Your study direction
            </p>
            <h2
              className="text-[24px] text-white tracking-tight mb-2"
              style={{ fontWeight: 900 }}
            >
              {answers.courseFinderInterests?.length
                ? answers.courseFinderInterests.join(", ")
                : "Course preferences saved"}
            </h2>
            <p
              className="text-[13px] text-violet-100 leading-relaxed"
              style={{ fontWeight: 400 }}
            >
              A counsellor will use this to confirm suitable courses,
              universities, entry requirements, fees, and scholarship options.
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {summaryItems.map((item) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
          >
            <div className="flex items-center gap-2 text-violet-500 mb-2">
              {item.icon}
              <span
                className="text-[11px] uppercase tracking-widest"
                style={{ fontWeight: 800 }}
              >
                {item.label}
              </span>
            </div>
            <p
              className="text-[14px] text-slate-800"
              style={{ fontWeight: 700 }}
            >
              {item.value}
            </p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-6"
      >
        <h3
          className="text-[15px] text-slate-900 mb-3"
          style={{ fontWeight: 800 }}
        >
          What happens next
        </h3>
        <div className="flex flex-col gap-2">
          {[
            "SCS checks course availability and entry requirements.",
            "A counsellor compares realistic university options.",
            "Fees, scholarships, rebates, and intake timelines are confirmed.",
          ].map((text) => (
            <div key={text} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
              <p
                className="text-[13px] text-slate-600"
                style={{ fontWeight: 400 }}
              >
                {text}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="flex flex-col sm:flex-row gap-3">
        <NavLink
          to="/course-finder"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-md hover:-translate-y-0.5 transition-all"
          style={{ fontWeight: 700 }}
        >
          Retake Course Finder <ArrowRight className="w-4 h-4" />
        </NavLink>
        <a
          href="https://wa.me/601112870256"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-slate-200 text-slate-700 text-[14px] hover:bg-slate-50 transition-all"
          style={{ fontWeight: 600 }}
        >
          <MessageCircle className="w-4 h-4" /> Talk to Counsellor
        </a>
      </div>
    </div>
  );
}
